import React from "react";
import { Phone, Mail, MapPin, MessageCircle, Truck } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const fmt = (v) => Number(v || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const TABLE_LABEL = { varejo: "Varejo", atacado: "Atacado", especial: "Especial" };

const Section = ({ title, text, color }) => (text ? (
  <div className="mt-2">
    <div className="text-[10px] font-extrabold uppercase tracking-wider cv-color" style={{ "--cv": color }}>{title}</div>
    <p className="text-[11px] leading-snug whitespace-pre-line text-gray-800">{text}</p>
  </div>
) : null);

const ProductCard = ({ it, accent }) => {
  const color = it.catalog_color || accent;
  return (
    <div className="rounded-xl overflow-hidden border bg-white flex flex-col" style={{ borderColor: color, breakInside: "avoid" }} data-testid={`catalog-item-${it.id}`}>
      <div className="flex items-center justify-between px-3 py-2 cv-white" style={{ background: color }}>
        <div className="font-extrabold text-lg leading-tight">{it.name}</div>
        {it.code && <span className="text-[10px] font-mono bg-white/25 rounded px-1.5">Cód. {it.code}</span>}
      </div>
      <div className="flex gap-3 p-3">
        <div className="w-28 h-28 shrink-0 rounded-lg bg-gray-100 flex items-center justify-center overflow-hidden">
          {it.photo_url
            ? <img src={`${BACKEND_URL}${it.photo_url}`} alt={it.name} crossOrigin="anonymous" className="w-full h-full object-contain" />
            : <span className="text-3xl font-black cv-color" style={{ "--cv": color }}>{(it.name || "?").slice(0, 4)}</span>}
        </div>
        <div className="flex-1 min-w-0">
          {it.catalog_headline && <span className="inline-block text-[10px] font-bold uppercase px-2 py-0.5 rounded-full cv-white" style={{ background: color }}>{it.catalog_headline}</span>}
          {it.description && <p className="text-xs text-gray-700 mt-1">{it.description}</p>}
          <div className="text-[11px] text-gray-500 mt-1">{it.catalog_weight || it.unit}</div>
          {it.price != null && (
            <div className="mt-1">
              <span className="text-2xl font-black cv-color" style={{ "--cv": color }} data-testid={`catalog-price-${it.id}`}>{fmt(it.price)}</span>
              <span className="text-xs text-gray-500"> / {it.unit}</span>
            </div>
          )}
        </div>
      </div>
      <div className="px-3 pb-3">
        <Section title="Indicações" text={it.catalog_indications} color={color} />
        <Section title="Modo de uso / Aplicação" text={it.catalog_application} color={color} />
        <Section title="Dados técnicos" text={it.catalog_technical} color={color} />
      </div>
    </div>
  );
};

const Footer = ({ cfg }) => (
  <div className="mt-6 pt-3 border-t text-xs text-gray-700 flex flex-wrap gap-x-5 gap-y-1">
    {cfg.whatsapp && <span className="flex items-center gap-1"><MessageCircle className="h-3 w-3" />{cfg.whatsapp}</span>}
    {cfg.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{cfg.phone}</span>}
    {cfg.email && <span className="flex items-center gap-1"><Mail className="h-3 w-3" />{cfg.email}</span>}
    {cfg.address && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{cfg.address}</span>}
    {cfg.footer_notes && <p className="w-full whitespace-pre-line text-[11px] text-gray-500">{cfg.footer_notes}</p>}
  </div>
);

export default function CatalogView({ data, freight }) {
  if (!data) return null;
  const cfg = data.config || {};
  const accent = cfg.accent_color || "#047857";
  const showPrices = data.items.some((i) => i.price != null);
  return (
    <div className="catalog-view bg-white p-6 text-gray-900" style={{ fontFamily: "'Segoe UI', Helvetica, Arial, sans-serif" }} data-testid="catalog-view">
      <style>{`.catalog-view .cv-white, .catalog-view .cv-white * { color: #fff !important; } .catalog-view .cv-color { color: var(--cv) !important; }`}</style>
      <div className="rounded-2xl p-5 cv-white mb-5" style={{ background: accent }}>
        <div className="text-3xl font-black tracking-tight">{data.company_name}</div>
        <div className="text-lg font-bold opacity-95">{cfg.title}</div>
        {cfg.subtitle && <div className="text-sm opacity-90 mt-1">{cfg.subtitle}</div>}
        {showPrices && <div className="text-xs mt-2 opacity-90">Tabela de preços: <b>{TABLE_LABEL[data.price_type] || data.price_type}</b> · {new Date().toLocaleDateString("pt-BR")}</div>}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {data.items.map((it) => <ProductCard key={it.id} it={it} accent={accent} />)}
      </div>
      {data.items.length === 0 && <p className="text-center text-sm text-gray-500 py-10">Nenhum produto no catálogo.</p>}
      {freight && (freight.value > 0 || freight.note) && (
        <div className="mt-5 rounded-xl border-2 p-3 flex items-center gap-3" style={{ borderColor: accent }} data-testid="catalog-freight">
          <Truck className="h-6 w-6 cv-color" style={{ "--cv": accent }} />
          <div>
            <div className="font-bold text-sm">Frete{freight.value > 0 ? `: ${fmt(freight.value)}` : ""}</div>
            {freight.note && <div className="text-xs text-gray-700">{freight.note}</div>}
          </div>
        </div>
      )}
      <Footer cfg={cfg} />
    </div>
  );
}
